import { useEffect, useRef, useState } from 'react';

import { BET_PRESETS, COLORS, MAX_BET, MAX_PLAYERS, MIN_PLAYERS, PACK_IDS, TURN_LIMITS_MS, getPack } from '@contagio/engine';
import type { BotDifficulty, PackId, RoomView } from '@contagio/engine';

import { LockGlyph, Mark } from '../art';
import { useLang, useT } from '../i18n';
import { PACK_ART } from '../packs';
import { PRICES, isUnlocked, unlock, useProgress } from '../profile';
import { LangToggle } from './LangToggle';
import { SoundMenu } from './SoundMenu';
import { ThemeToggle } from './ThemeToggle';

interface LobbyProps {
  room: RoomView;
  youId: string | null;
  onAddBot: () => Promise<unknown>;
  onRemove: (playerId: string) => Promise<unknown>;
  onDifficulty: (difficulty: BotDifficulty) => Promise<unknown>;
  onPack: (pack: PackId) => Promise<unknown>;
  onTurnLimit: (ms: number) => Promise<unknown>;
  onBet: (amount: number) => Promise<unknown>;
  onStart: () => Promise<unknown>;
  onLeave: () => void;
  onShowRules: () => void;
}

const DIFFICULTIES: BotDifficulty[] = ['easy', 'normal', 'hard'];

/** Con la que se abre al encender las apuestas: el atajo mas bajo. */
const FIRST_BET = BET_PRESETS[0] ?? 0;

export function Lobby({
  room,
  youId,
  onAddBot,
  onRemove,
  onDifficulty,
  onPack,
  onTurnLimit,
  onBet,
  onStart,
  onLeave,
  onShowRules,
}: LobbyProps) {
  const t = useT();
  const lang = useLang();
  const progress = useProgress();
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  /** Paquete bloqueado que se esta mirando, con su precio a la vista. */
  const [wanted, setWanted] = useState<PackId | null>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);
  /**
   * Lo tecleado en la apuesta libre mientras se teclea. No se manda letra a
   * letra: un 2 camino de 200 seria una apuesta de 2 para toda la mesa.
   */
  const [typedBet, setTypedBet] = useState<string | null>(null);

  // La sala ya no cabe entera en una ventana: si el servidor rechaza empezar
  // (alguien no cubre la apuesta), el motivo puede quedar fuera de la pantalla.
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ block: 'nearest' });
  }, [error]);
  const isHost = room.players.some((p) => p.id === youId && p.isHost);
  const canStart = room.players.length >= MIN_PLAYERS;
  const humans = room.players.filter((p) => !p.isBot).length;
  /** El bote solo existe entre personas: los bots no ponen. */
  const pot = humans > 1 ? room.bet * humans : 0;

  async function guard(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.failed);
    }
  }

  function copyCode() {
    navigator.clipboard
      ?.writeText(room.code)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      })
      .catch(() => setError(t.lobby.copyByHand(room.code)));
  }

  /** Deja la apuesta escrita, o devuelve el campo a lo que hay puesto. */
  function commitBet() {
    const amount = Math.floor(Number(typedBet));
    setTypedBet(null);
    if (!Number.isFinite(amount) || amount < 1 || amount > MAX_BET || amount === room.bet) return;
    void guard(() => onBet(amount));
  }

  function buy(id: PackId) {
    if (!unlock(id)) return;
    setWanted(null);
    // Recien pagado: si eres el anfitrion, la sala se viste con el en el acto,
    // que es para lo que se ha pagado. Un invitado solo lo suma a su coleccion.
    if (isHost) void guard(() => onPack(id));
  }

  return (
    <div className="lobby">
      <div className="lobby__sheet">
        <header className="lobby__head">
          <div>
            <h1 className="lobby__title">
              <Mark className="lobby__mark" />
              {t.lobby.title}
            </h1>
            <p className="lobby__lead">{t.lobby.lead(MIN_PLAYERS, MAX_PLAYERS)}</p>
          </div>
          <div className="lobby__tools">
            <LangToggle />
            <SoundMenu />
            <ThemeToggle compact />
            <button type="button" className="codechip mono" onClick={copyCode} title={t.lobby.copyTitle}>
              <span className="codechip__code">{room.code}</span>
              <span className="codechip__hint">{copied ? t.lobby.copied : t.lobby.copy}</span>
            </button>
          </div>
        </header>

        <ol className="roster">
          {room.players.map((player, index) => (
            <li key={player.id} className={`roster__row ${player.id === youId ? 'is-you' : ''}`}>
              <span className="roster__index mono">{String(index + 1).padStart(2, '0')}</span>
              <span className="roster__name">
                {player.name}
                {player.isHost && <span className="tag">{t.common.host}</span>}
                {player.isBot && <span className="tag">{t.common.bot}</span>}
                {player.id === youId && <span className="tag tag--you">{t.common.you}</span>}
              </span>
              {room.bet > 0 && player.points !== null && (
                <span
                  className={`roster__points mono ${player.points < room.bet ? 'is-short' : ''}`}
                  title={t.progress.points(player.points)}
                >
                  {player.points}
                </span>
              )}
              {isHost && player.id !== youId && (
                <button type="button" className="roster__remove" onClick={() => void guard(() => onRemove(player.id))}>
                  {t.lobby.remove}
                </button>
              )}
            </li>
          ))}
          {Array.from({ length: MAX_PLAYERS - room.players.length }).map((_, i) => (
            <li key={`empty-${i}`} className="roster__row roster__row--empty">
              <span className="roster__index mono">{String(room.players.length + i + 1).padStart(2, '0')}</span>
              <span className="roster__name">{t.lobby.emptySeat}</span>
              {isHost && i === 0 && (
                <button type="button" className="roster__add" onClick={() => void guard(onAddBot)}>
                  {t.lobby.addBot}
                </button>
              )}
            </li>
          ))}
        </ol>

        {/* Todos ven la eleccion; solo el anfitrion la cambia. */}
        <section className="packs" aria-labelledby="packs-title">
          <h2 id="packs-title" className="packs__title">
            {t.lobby.packs}
            <span className="packs__points mono">{t.progress.points(progress.points)}</span>
          </h2>
          <div className="packs__grid" role="group" aria-labelledby="packs-title">
            {PACK_IDS.map((id) => {
              const art = PACK_ART[id];
              const active = room.pack === id;
              const locked = !isUnlocked(id, progress);
              return (
                <button
                  key={id}
                  type="button"
                  aria-pressed={active}
                  aria-label={locked ? t.progress.locked(getPack(id, lang).name, PRICES[id]) : undefined}
                  data-pack={id}
                  className={`packtile ${active ? 'is-active' : ''} ${locked ? 'packtile--locked' : ''}`}
                  // Un paquete bloqueado se puede tocar aunque no seas el
                  // anfitrion: la coleccion es tuya y se paga desde cualquier silla.
                  disabled={!isHost && !locked}
                  onClick={() => {
                    if (locked) return setWanted(id);
                    // Elegir otro paquete retira la oferta: vuelve su reclamo.
                    setWanted(null);
                    if (!active) void guard(() => onPack(id));
                  }}
                >
                  <span className="packtile__glyphs">
                    {COLORS.map((color) => {
                      const Glyph = art.organs[color];
                      return <Glyph key={color} className={`packtile__glyph tone-${color}`} />;
                    })}
                  </span>
                  <span className="packtile__name">{getPack(id, lang).name}</span>
                  {locked && (
                    <span className="packtile__lock mono" aria-hidden>
                      <LockGlyph className="packtile__padlock" />
                      {PRICES[id]}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {/* El mismo hueco para las dos cosas: mirar un precio no mueve la sala. */}
          {wanted ? (
            <p className="packs__tagline packs__offer">
              <span className="packs__offer-name">
                {getPack(wanted, lang).name} · {t.progress.price(PRICES[wanted])}
              </span>
              {progress.points >= PRICES[wanted] ? (
                <button type="button" className="btn btn--outline packs__buy" onClick={() => buy(wanted)}>
                  {t.progress.unlock}
                </button>
              ) : (
                <span className="packs__missing">{t.progress.missing(PRICES[wanted] - progress.points)}</span>
              )}
            </p>
          ) : (
            <p className="packs__tagline">{getPack(room.pack, lang).tagline}</p>
          )}
        </section>

        {isHost && (
          <div className="lobby__settings">
            <div className="setting">
              <span id="difficulty-label" className="setting__label">
                {t.lobby.difficulty}
              </span>
              <div className="segmented" role="group" aria-labelledby="difficulty-label">
                {DIFFICULTIES.map((level) => (
                  <button
                    key={level}
                    type="button"
                    aria-pressed={room.difficulty === level}
                    className={`segmented__option ${room.difficulty === level ? 'is-active' : ''}`}
                    onClick={() => void guard(() => onDifficulty(level))}
                  >
                    {t.lobby.levels[level]}
                  </button>
                ))}
              </div>
            </div>
            <div className="setting">
              <span id="turn-label" className="setting__label">
                {t.lobby.turn}
              </span>
              <div className="segmented" role="group" aria-labelledby="turn-label">
                {TURN_LIMITS_MS.map((ms) => (
                  <button
                    key={ms}
                    type="button"
                    aria-pressed={room.turnLimitMs === ms}
                    className={`segmented__option ${room.turnLimitMs === ms ? 'is-active' : ''}`}
                    onClick={() => room.turnLimitMs !== ms && void guard(() => onTurnLimit(ms))}
                  >
                    {t.lobby.seconds(ms / 1000)}
                  </button>
                ))}
              </div>
            </div>
            <div className="setting">
              <span id="bets-label" className="setting__label">
                {t.lobby.bets}
              </span>
              {/* Interruptor y cantidades en la misma fila: las cantidades no
                  aparecen al encender, estan siempre y apagadas, para que
                  encender no mueva el boton de empezar ni alargue la sala. */}
              <div className="betrow">
                <button
                  type="button"
                  role="switch"
                  aria-checked={room.bet > 0}
                  aria-labelledby="bets-label"
                  className={`switch ${room.bet > 0 ? 'is-on' : ''}`}
                  onClick={() => void guard(() => onBet(room.bet > 0 ? 0 : FIRST_BET))}
                >
                  <span className="switch__track" aria-hidden>
                    <span className="switch__knob" />
                  </span>
                  <span className="switch__state">{room.bet > 0 ? t.common.on : t.common.off}</span>
                </button>
                <div className="segmented" role="group" aria-label={t.lobby.betPresets}>
                  {BET_PRESETS.map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      aria-pressed={room.bet === amount}
                      disabled={room.bet === 0}
                      className={`segmented__option ${room.bet === amount ? 'is-active' : ''}`}
                      onClick={() => {
                        setTypedBet(null);
                        if (room.bet !== amount) void guard(() => onBet(amount));
                      }}
                    >
                      {amount}
                    </button>
                  ))}
                </div>
                {/* Los atajos son eso, atajos: la apuesta de verdad es esta, y
                    se manda al salir del campo o con la tecla de entrar. */}
                <input
                  className="betfield mono"
                  type="text"
                  inputMode="numeric"
                  maxLength={String(MAX_BET).length}
                  aria-label={t.lobby.betAmount}
                  disabled={room.bet === 0}
                  value={typedBet ?? (room.bet === 0 ? '' : String(room.bet))}
                  onChange={(e) => setTypedBet(e.target.value.replace(/[^0-9]/g, ''))}
                  onBlur={commitBet}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                    if (e.key === 'Escape') setTypedBet(null);
                  }}
                />
              </div>
            </div>
            <button
              type="button"
              className="btn btn--primary lobby__start"
              onClick={() => void guard(onStart)}
              disabled={!canStart}
            >
              {t.lobby.start}
            </button>
          </div>
        )}

        {!isHost && (
          <p className="notice">{t.lobby.hostStarts(Math.round(room.turnLimitMs / 1000))}</p>
        )}

        {/* La apuesta sale de los puntos de cada cual, asi que la ve toda la
            mesa y no solo quien la pone. */}
        {room.bet > 0 && (
          <p className="notice notice--bet">
            {pot > 0 ? t.lobby.betOn(room.bet, pot) : t.lobby.betAlone}
          </p>
        )}

        {error && (
          <p ref={errorRef} className="notice notice--error">
            {error}
          </p>
        )}

        <footer className="lobby__foot">
          <button type="button" className="btn btn--ghost" onClick={onShowRules}>
            {t.common.rules}
          </button>
          <button type="button" className="btn btn--ghost" onClick={onLeave}>
            {t.lobby.leave}
          </button>
        </footer>
      </div>
    </div>
  );
}
